import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BackButton, Button, Card, cn, TypePill, VoiceOrb } from '@wundo/ui';
import type {
  RoleplayCard,
  RoleplayDebrief,
  RoleplayMode,
  RoleplaySessionResponse,
  VoiceConnection,
} from '@wundo/shared/roleplay.ts';
import { PracticeChat } from './PracticeChat.tsx';
import { PracticeSessionBrief } from './PracticeSessionBrief.tsx';
import { PracticeSessionBriefPanes } from './PracticeSessionBriefPanes.tsx';
import { PracticeSessionEnd } from './PracticeSessionEnd.tsx';
import { PracticeSessionStart } from './PracticeSessionStart.tsx';
import { PracticeVoiceCall } from './PracticeVoiceCall.tsx';
import { PracticeVoiceSetup } from './PracticeVoiceSetup.tsx';
import { PracticeVoiceStage } from './PracticeVoiceStage.tsx';
import { PracticeWrapUpDialog } from './PracticeWrapUpDialog.tsx';
import { formatElapsed } from './practiceFormat.ts';
import { useElapsedSeconds } from './useElapsedSeconds.ts';
import { DEBRIEF_POLL_MS, DEBRIEF_WAIT_LIMIT_MS } from './debriefWait.ts';
import {
  beginRoleplaySession,
  endRoleplaySession,
  fetchRoleplaySession,
} from '@/lib/roleplaySession.ts';
import type { VoiceAgentState } from '@/lib/roleplayCall.ts';
import { fetchRoleplays } from '@/lib/roleplays.ts';
import { useAudioSetup } from '@/lib/useAudioSetup.ts';
import { m } from '@/paraglide/messages.js';

/** How long a practice conversation runs before it is asked whether it is done. */
const WRAP_UP_NUDGE_MS = 10 * 60 * 1_000;

/**
 * How much room the conversation is asking its host for. `brief` and `ended`
 * are read-and-click screens that a host can size to their content; `live` is
 * a conversation that wants all the height it can get.
 */
export type PracticeSessionPhase = 'brief' | 'live' | 'ended';

/** What the conversation is doing, once the session itself has loaded. */
interface LiveConversation {
  mode: RoleplayMode;
  /** Null in a written conversation, and until a spoken one is granted a call. */
  voice: VoiceConnection | null;
}

export interface PracticeSessionExperienceProps {
  session: RoleplaySessionResponse;
  /**
   * What stands in front of the persona on the header row, with the hairline
   * that separates the two drawn here. The dedicated address puts the logo
   * there because it is outside the app and nothing else on the screen says
   * whose it is; the modal on Oefenen has the app around it.
   */
  brand?: ReactNode;
  /** Back to wherever the host came from. Must be stable across renders. */
  onLeave: () => void;
  /**
   * Hands the finished feedback to the host instead of showing it here. Null
   * when nobody spoke and there is none to show. Must be stable across renders.
   *
   * For a host that is already standing on Oefenen: closing it to reopen the
   * same feedback in a second popup on the page underneath is a flash of the
   * list for nothing. A modal reads it in its own idiom — a dialog header over
   * the report — which it can only do if the report is its to render.
   *
   * The conversation renders nothing for that state: the host takes over, and
   * whatever it does with the feedback, leaving is still `onLeave`.
   */
  onDebrief?: (debrief: RoleplayDebrief | null) => void;
  /**
   * Told whenever the phase changes, so a host that sizes itself to the
   * conversation can follow it. Must be stable across renders.
   */
  onPhaseChange?: (phase: PracticeSessionPhase) => void;
}

/**
 * One practice conversation, from the moment it is opened to the moment the
 * feedback exists.
 *
 * Everything on it is read from the session, so the conversation survives a
 * reload: somebody who refreshes mid-conversation lands back in the same
 * conversation rather than at the start of a new one.
 *
 * It knows nothing about where it is being shown. Leaving tells its host and
 * lets it decide what that means — a modal on Oefenen closes, the dedicated
 * address navigates.
 *
 * The feedback is read right here, on the screen the conversation was held on.
 * Only a host that asks for it is handed it instead, and shows it in its own
 * idiom.
 */
export function PracticeSessionExperience({
  session,
  brand,
  onLeave,
  onDebrief,
  onPhaseChange,
}: PracticeSessionExperienceProps) {
  const queryClient = useQueryClient();
  const { persona } = session.scenario;

  /*
   * Someone who came in on a link, rather than signed in. Everything the rest
   * of the app offers is closed to them, so nothing here points at it.
   */
  const linkOnly = session.scoped_access;

  const [mode, setMode] = useState<RoleplayMode>('voice');
  /* The audio check, between the brief and a spoken conversation. */
  const [setup, setSetup] = useState(false);
  const [live, setLive] = useState<LiveConversation | null>(
    session.status === 'in_progress' && session.mode !== null
      ? { mode: session.mode, voice: session.voice ?? null }
      : null,
  );
  const [agentState, setAgentState] = useState<VoiceAgentState | null>(null);
  const [captionsVisible, setCaptionsVisible] = useState(true);
  const [wrapUp, setWrapUp] = useState(false);
  const [nudged, setNudged] = useState(false);
  const [nudgeOpen, setNudgeOpen] = useState(false);
  /*
   * How to hang up, whichever kind of call this turns out to be. Handed up by
   * the call itself, because only it knows what it is holding.
   */
  const hangUp = useRef<(() => Promise<void>) | null>(null);

  /*
   * The microphone is only asked for on the audio check, and released the
   * moment the call takes it over — two things holding the same device is how
   * a conversation starts silent.
   */
  const audio = useAudioSetup(live === null && setup);

  /*
   * Where a conversation ends on this screen. Undefined while it is still
   * running; a debrief, or null when nobody spoke and there is none to write.
   *
   * Nobody is sent to Oefenen to read it: half of the people who reach this
   * screen have no Oefenen at all, and the rest were handed a link in a chat
   * message or an email rather than coming from the list. Dropping them on a
   * page they never asked for, to read the report of the conversation they
   * just finished, is a page too far. Only a host that asks for the feedback
   * keeps it out of here — it is showing it itself.
   */
  const [endedHere, setEndedHere] = useState<RoleplayDebrief | null | undefined>(
    undefined,
  );
  const ended: RoleplayDebrief | null | undefined =
    onDebrief !== undefined
      ? undefined
      : endedHere !== undefined
        ? endedHere
        : session.status === 'aborted'
          ? null
          : session.status === 'completed'
            ? session.debrief
            : undefined;

  const phase: PracticeSessionPhase =
    ended !== undefined ? 'ended' : live !== null ? 'live' : 'brief';
  useEffect(() => {
    onPhaseChange?.(phase);
  }, [phase, onPhaseChange]);

  /*
   * Each end that answers `completing` is a new ask for the feedback, with a
   * wait of its own. The wait that ran out is remembered by its ask, so the
   * next one starts clean.
   */
  const [asked, setAsked] = useState(0);
  const [gaveUpOn, setGaveUpOn] = useState<number | null>(null);

  const end = useMutation({
    mutationFn: async () => {
      /*
       * Hang up before asking for the feedback: writing it takes a second or
       * two, and buffered speech would keep playing right through it.
       */
      await hangUp.current?.();
      return endRoleplaySession(session.id);
    },
    onSuccess: (result) => {
      setNudgeOpen(false);
      refresh();

      /*
       * Being written: the session is read again until it is, and the screen
       * below follows it.
       */
      if (result.status === 'completing') {
        setAsked((count) => count + 1);
        queryClient.setQueryData<RoleplaySessionResponse>(
          ['roleplaySession', session.id],
          (current) => current && { ...current, status: result.status },
        );
        return;
      }

      /* A host that reads the feedback itself is handed it and takes over. */
      if (onDebrief !== undefined) {
        onDebrief(result.debrief);
        return;
      }

      /*
       * Otherwise the feedback stays where the conversation was, and so does
       * the reader — including when there is none, because nobody spoke.
       */
      setEndedHere(result.debrief);
    },
    /*
     * Refused, most likely because the feedback was settled after this screen
     * stopped asking. Reading the session again shows where it stands.
     */
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: ['roleplaySession', session.id] });
    },
  });

  /*
   * Asked for every couple of seconds, but not forever: a job the server lost
   * would keep this screen waiting for nothing. Past the point where the
   * server has settled it either way, the wait counts as failed.
   */
  const waiting = session.status === 'completing';
  const waitedTooLong = waiting && gaveUpOn === asked;
  useEffect(() => {
    if (!waiting) {
      return;
    }

    const timer = setTimeout(() => setGaveUpOn(asked), DEBRIEF_WAIT_LIMIT_MS);
    return () => clearTimeout(timer);
  }, [waiting, asked]);

  useQuery({
    queryKey: ['roleplaySession', session.id],
    queryFn: () => fetchRoleplaySession(session.id),
    enabled: waiting && !waitedTooLong,
    refetchInterval: DEBRIEF_POLL_MS,
  });

  const ending = end.isPending || (waiting && !waitedTooLong);
  /* Writing the feedback gave up, or never came. Ending again asks once more. */
  const debriefFailed =
    !end.isPending && (session.status === 'debrief_failed' || waitedTooLong);

  /*
   * The clock starts when the other side is actually there: in a spoken
   * conversation that is when the call connects, which can be a few seconds
   * after the button was pressed. It stops the instant Beëindig is pressed —
   * what it is showing is how long the conversation was, and the wait for the
   * feedback is not part of it.
   */
  const running =
    ended === undefined &&
    !ending &&
    !debriefFailed &&
    live !== null &&
    (live.mode === 'chat' || agentState !== null);
  const elapsed = useElapsedSeconds(running);

  /*
   * The library card this attempt belongs to, for the type on the header. Not
   * asked for at all on a link visit: the library is not theirs to read, and
   * the type is a nicety the header does without.
   */
  const { data: roleplays } = useQuery({
    queryKey: ['roleplays'],
    queryFn: fetchRoleplays,
    enabled: !linkOnly,
  });
  const card: RoleplayCard | undefined =
    session.roleplay_id === null
      ? undefined
      : [...(roleplays?.library ?? []), ...(roleplays?.personal ?? [])].find(
          (candidate) => candidate.id === session.roleplay_id,
        );

  /**
   * What was practised has changed, so the attempt counts and the feedback are
   * asked for again rather than read from the cache this screen was opened
   * from — the new attempt is the whole reason to go back.
   */
  const roleplayId = session.roleplay_id;
  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['roleplays'] });
    if (roleplayId !== null) {
      void queryClient.invalidateQueries({
        queryKey: ['roleplayAttempts', roleplayId],
      });
    }
  }, [queryClient, roleplayId]);

  /**
   * Back to whatever this conversation is being held over, with what was
   * practised counted again on the way out.
   */
  function leave() {
    refresh();
    onLeave();
  }

  /*
   * The mode travels with the mutation rather than being read back from state:
   * "practise in chat instead" switches it and starts in the same click.
   */
  const begin = useMutation({
    mutationFn: async (chosen: RoleplayMode) => beginRoleplaySession(session.id, chosen),
    onSuccess: (voice, chosen) => setLive({ mode: chosen, voice }),
  });

  /**
   * The way out before there is anything to end.
   *
   * Nothing has been said, so nothing is ended: the session stays where it is
   * and the link still opens it. Somebody signed in goes back to Oefenen;
   * somebody on a link has nowhere to go, so the screen closes itself.
   */
  function abandon() {
    if (linkOnly) {
      setEndedHere(null);
      return;
    }

    leave();
  }

  /*
   * A session that is over, whether it already was when this screen opened or
   * its feedback was written while the screen waited on it.
   *
   * A host that reads the feedback itself is handed it, whether it was written
   * or the conversation ended before anybody spoke. Everywhere else it is
   * already on screen: the end screen below is what this renders.
   */
  const { status, debrief } = session;
  useEffect(() => {
    if (status === 'completed') {
      refresh();
    }

    if (onDebrief === undefined) {
      return;
    }

    if (status !== 'completed' && status !== 'aborted') {
      return;
    }

    onDebrief(status === 'completed' ? debrief : null);
  }, [status, debrief, onDebrief, refresh]);

  /*
   * Ten minutes in, once. A practice conversation has no natural end, and the
   * feedback is written from the whole of it — one that runs on gets a debrief
   * about half an hour of wandering.
   *
   * Dropped entirely if the character has already said the same thing in its
   * own words: two ways of being asked to wrap up is nagging.
   */
  useEffect(() => {
    if (!running || nudged || wrapUp) {
      return;
    }

    const timer = setTimeout(() => {
      setNudged(true);
      setNudgeOpen(true);
    }, WRAP_UP_NUDGE_MS);

    return () => clearTimeout(timer);
  }, [running, nudged, wrapUp]);

  /*
   * Over, and the feedback is the host's to show. Nothing here belongs on that
   * screen — least of all the persona and the clock of a conversation that is
   * finished, standing over its own debrief.
   */
  if (
    onDebrief !== undefined &&
    (session.status === 'completed' || session.status === 'aborted')
  ) {
    return null;
  }

  return (
    <>
      {/*
       * Once the conversation is over the header is down to whose page this
       * is: everything else on it is about a conversation that has ended. The
       * logo stays, because a link visitor has nothing else saying whose it is.
       */}
      {(ended === undefined || brand !== undefined) && (
        <header className='shrink-0 border-b border-line bg-surface px-5 py-4.5'>
          <div className='mx-auto flex w-full max-w-240 flex-wrap items-center gap-x-3 gap-y-2'>
            {brand}
            {ended === undefined && (
              <>
                {brand !== undefined && (
                  <span aria-hidden='true' className='h-5 w-px bg-line' />
                )}
                <h1 className='min-w-0 truncate text-md font-semibold text-ink'>
                  {`${persona.name}, ${persona.role}`}
                </h1>
                {card?.type_label != null && (
                  <TypePill type='none'>{card.type_label}</TypePill>
                )}

                <div className='ml-auto flex items-center gap-4'>
                  {/*
                   * Before the start there is nothing to end, but there is
                   * always a way out: this screen covers everything under it.
                   * A link visitor has no Oefenen to go back to, so they close.
                   */}
                  {live === null && linkOnly ? (
                    <Button variant='ghost' onClick={abandon}>
                      {m.practice_close()}
                    </Button>
                  ) : live === null ? (
                    <BackButton onClick={abandon}>{m.practice_back()}</BackButton>
                  ) : (
                    <>
                      <span
                        aria-label={m.practice_session_elapsed()}
                        className='text-base font-medium tracking-wide text-ink-secondary tabular-nums'
                      >
                        {formatElapsed(elapsed)}
                      </span>
                      <Button disabled={ending} onClick={() => end.mutate()}>
                        {m.practice_session_end()}
                      </Button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </header>
      )}

      {ended !== undefined ? (
        <div className='min-h-0 flex-1 overflow-y-auto px-5 pt-6.5 pb-16'>
          <PracticeSessionEnd debrief={ended} />
        </div>
      ) : live === null ? (
        <div className='flex min-h-0 flex-1 flex-col px-5 pt-6.5 pb-6.5'>
          <div className='mx-auto flex min-h-0 w-full max-w-240 flex-1 flex-col gap-4.5'>
            <PracticeSessionBriefPanes scenario={session.scenario} />

            {/*
             * Outside the panes' scroll, so the start stays on screen however
             * long the brief is — on a phone too, where the panes stack.
             */}
            <div className='shrink-0'>
              {setup ? (
                <PracticeVoiceSetup
                  audio={audio}
                  starting={begin.isPending}
                  failed={begin.isError}
                  onStart={() => begin.mutate('voice')}
                  onBack={() => setSetup(false)}
                  onChatInstead={() => {
                    setMode('chat');
                    begin.mutate('chat');
                  }}
                />
              ) : (
                <PracticeSessionStart
                  mode={mode}
                  starting={begin.isPending}
                  failed={begin.isError}
                  onModeChange={setMode}
                  onBegin={() =>
                    mode === 'voice' ? setSetup(true) : begin.mutate('chat')
                  }
                />
              )}
            </div>
          </div>
        </div>
      ) : (
        /*
         * The conversation comes first and the situation after it. On a phone
         * that ordering is the layout: the brief above the stage pushed the orb
         * off the bottom of the screen, so the one thing the user is looking at
         * was the one thing they had to scroll to find.
         */
        <div
          className={cn(
            'min-h-0 flex-1 overflow-y-auto px-5 pt-6.5 agenda:overflow-hidden',
            /*
             * The dock floats over the bottom of the window rather than in the
             * column, so the column has to stop above it. Without this the
             * white card runs on underneath the controls.
             */
            live.mode === 'voice' ? 'pb-32.5' : 'pb-6.5',
          )}
        >
          {/*
            A flex row rather than a grid: a grid track grows with a long
            situation and carries the stage off the bottom of the window. In a
            flex row the height is the window's, whatever is written in it.
          */}
          <div className='mx-auto flex w-full max-w-240 flex-col gap-4.5 agenda:h-full agenda:flex-row agenda:items-stretch'>
            <div className='flex min-h-95 flex-col gap-3.5 agenda:order-2 agenda:min-h-0 agenda:flex-1'>
              {end.isError && (
                <p role='alert' className='text-base text-danger'>
                  {m.practice_session_end_failed()}
                </p>
              )}

              {wrapUp && (
                <Card tone='accent' className='flex flex-wrap items-center gap-3.5'>
                  <p className='min-w-0 flex-1 text-base text-ink'>
                    {m.practice_session_suggest_end()}
                  </p>
                  <Button size='sm' disabled={ending} onClick={() => end.mutate()}>
                    {m.practice_session_end()}
                  </Button>
                </Card>
              )}

              {live.mode === 'chat' ? (
                <PracticeChat
                  sessionId={session.id}
                  personaName={persona.name}
                  onSuggestEnd={() => setWrapUp(true)}
                />
              ) : live.voice !== null ? (
                <PracticeVoiceCall
                  connection={live.voice}
                  personaName={persona.name}
                  hangUpRef={hangUp}
                  agentState={agentState}
                  captionsVisible={captionsVisible}
                  microphoneId={audio.microphoneId}
                  speakerId={audio.speakerId}
                  leaving={ending}
                  onAgentState={setAgentState}
                  onSuggestEnd={() => setWrapUp(true)}
                  onCallEnded={() => {
                    if (!ending) {
                      end.mutate();
                    }
                  }}
                  onToggleCaptions={() => setCaptionsVisible((visible) => !visible)}
                />
              ) : (
                /* A spoken conversation with no call to join yet: still connecting. */
                <PracticeVoiceStage
                  personaName={persona.name}
                  agentState={null}
                  captionsVisible={captionsVisible}
                />
              )}
            </div>

            {/* 320px, the width the reference gives it. */}
            <div className='agenda:order-1 agenda:min-h-0 agenda:w-80 agenda:shrink-0 agenda:overflow-y-auto'>
              <PracticeSessionBrief scenario={session.scenario} />
            </div>
          </div>
        </div>
      )}

      {nudgeOpen && (
        <PracticeWrapUpDialog
          ending={ending}
          onEnd={() => end.mutate()}
          onKeepGoing={() => setNudgeOpen(false)}
        />
      )}

      {(ending || debriefFailed) && (
        /*
         * Opaque, and over the surface this conversation is on rather than over
         * the window: the session behind it is over, and half a ghosted stage
         * under a translucent film reads as a rendering fault rather than as
         * "we are writing your feedback".
         */
        <div className='absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-canvas px-5 text-center'>
          {debriefFailed ? (
            <>
              <p role='alert' className='text-base text-ink-body'>
                {m.practice_session_debrief_failed()}
              </p>
              <div className='flex flex-wrap items-center justify-center gap-3'>
                <Button onClick={() => end.mutate()}>
                  {m.practice_session_debrief_retry()}
                </Button>
                {!linkOnly && (
                  <Button variant='quiet' onClick={leave}>
                    {m.practice_back()}
                  </Button>
                )}
              </div>
            </>
          ) : (
            <>
              <VoiceOrb state='thinking' />
              <p className='text-base text-ink-body'>{m.practice_session_ending()}</p>
            </>
          )}
        </div>
      )}
    </>
  );
}
